import type { CallListItem } from "./api.js";

export type TranscriptSpeaker = "agent" | "lead" | "unknown";

export interface TranscriptLine {
  id: string;
  speaker: TranscriptSpeaker;
  text: string;
  /** Offset opcional; o contrato atual só entrega texto, mas o parser aceita a evolução. */
  startSeconds: number | null;
}

export interface CallTimelineEvent {
  id: "created" | "started" | "ended";
  at: string;
  offsetSeconds: number | null;
}

function finiteOffset(value: unknown, duration: number | null): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return null;
  return duration == null ? value : Math.min(value, Math.max(0, duration));
}

/**
 * Normaliza a transcrição atual ([Agente]/[Lead]) sem descartar linhas livres.
 * Também aceita o formato futuro de segmentos, mantendo timestamp inválido nulo.
 */
export function normalizeTranscript(
  value: string | Array<{ speaker?: unknown; text?: unknown; startSeconds?: unknown }> | null,
  duration: number | null = null,
): TranscriptLine[] {
  if (!value) return [];
  const raw = typeof value === "string" ? value.split(/\r?\n+/) : value;
  return raw.flatMap((entry, index) => {
    const text = typeof entry === "string" ? entry.trim() : String(entry.text ?? "").trim();
    if (!text) return [];
    const tagged = text.match(/^\[(agente|agent|lead)\]\s*:?[ \t]*(.*)$/i);
    const speakerValue = typeof entry === "string" ? tagged?.[1] : entry.speaker;
    const speaker = /^(agente|agent)$/i.test(String(speakerValue ?? ""))
      ? "agent"
      : /^lead$/i.test(String(speakerValue ?? ""))
        ? "lead"
        : "unknown";
    return [
      {
        id: `transcript-${index}`,
        speaker,
        text: typeof entry === "string" && tagged ? tagged[2].trim() : text,
        startSeconds: typeof entry === "string" ? null : finiteOffset(entry.startSeconds, duration),
      } satisfies TranscriptLine,
    ];
  });
}

export function buildCallTimeline(
  call: Pick<CallListItem, "createdAt" | "startedAt" | "endedAt" | "duration">,
): CallTimelineEvent[] {
  const started = call.startedAt ? new Date(call.startedAt).getTime() : null;
  return [
    { id: "created" as const, at: call.createdAt, offsetSeconds: 0 },
    ...(call.startedAt ? [{ id: "started" as const, at: call.startedAt, offsetSeconds: 0 }] : []),
    ...(call.endedAt
      ? [
          {
            id: "ended" as const,
            at: call.endedAt,
            offsetSeconds: finiteOffset(call.duration, call.duration),
          },
        ]
      : []),
  ].map((event) => ({
    ...event,
    offsetSeconds:
      event.id === "created" || started == null
        ? event.offsetSeconds
        : event.id === "started"
          ? 0
          : finiteOffset((new Date(event.at).getTime() - started) / 1000, call.duration),
  }));
}
